// AGE-BUG(row-click-controls) · vendor/age-design/SOURCE_COMMIT 7322a157
// The shared guard for a register whose whole row opens something.
//
// `mud-table` emits `mudRowClick` for every click in a body row except those
// that land on `mud-button, mud-checkbox, a, button` (mud-table.js:214). It
// tests `event.target.closest(...)` from its own shadow root, where a click on
// the anchor inside a `mud-link` arrives retargeted to the `mud-link` host, so
// `closest('a')` misses it. A `RouterLink` in a clickable row therefore opens
// its own page AND the row's; the same holds for an input or a switch.
//
// A second case is not AGE's to know about: selecting text in a cell — copying
// a code out of the Nomenclator — ends in a click, and that click must not
// take the reader away from what they were copying.
//
// `mudRowClick` carries only `{ row, index }`, never the click behind it, so
// the click is read where it starts: a capture listener on the document sees
// it before the row does, in the same dispatch, and records whether it came
// from a control. The day AGE's filter covers its own controls,
// `grep -rn 'AGE-BUG(row-click-controls)'` finds this helper; the text-
// selection check stays.

/** Anything in a row that answers a click of its own. */
const CONTROL = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="checkbox"]',
  '[role="switch"]',
  'mud-link',
  'mud-button',
  'mud-checkbox',
  'mud-switch',
  'mud-radio',
  'mud-text-input',
  'mud-select',
].join(', ');

let lastClickFromControl = false;
let installed = false;

/** True when the click's path, from its target up to the row, crosses a control. */
export function pathCrossesControl(path: readonly EventTarget[]): boolean {
  for (const node of path) {
    if (!(node instanceof Element)) continue;
    const tag = node.tagName;
    // Past the row nothing belongs to it; the table itself is not a control.
    if (tag === 'TR' || tag === 'MUD-TABLE') return false;
    if (node.matches(CONTROL)) return true;
  }
  return false;
}

function install(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener(
    'click',
    (event) => {
      lastClickFromControl = pathCrossesControl(event.composedPath());
    },
    true,
  );
}

function selectingText(): boolean {
  const selection = typeof window === 'undefined' ? null : window.getSelection();
  return selection !== null && !selection.isCollapsed && selection.toString().trim() !== '';
}

/**
 * Wraps a `<MudTable onMudRowClick>` handler so the row opens only for a click
 * on the row itself: not on a link, button or field inside it, and not at the
 * end of a text selection.
 *
 * @example
 * ```tsx
 * <MudTable hoverable onMudRowClick={onRowOpen((index) => navigate(pathOf(rows[index])))} />
 * ```
 */
export function onRowOpen<Detail extends { index: number } = { index: number }>(
  open: (index: number, detail: Detail) => void,
): (event: CustomEvent<Detail>) => void {
  install();
  return (event) => {
    if (lastClickFromControl || selectingText()) return;
    open(event.detail.index, event.detail);
  };
}
